// userManagement.ts

import { appLog } from '~src/utils/log';
import { apiCweb } from '~src/services/api/api-cweb';

// Мова для email з кодом
export type UserManagementLanguage = 'en' | 'da' | 'de' | 'sv';

/**
 * POST /user_management/tokens/user_password_forgotten_token
 * Create token for user password forgotten
 */
export const createUserPasswordForgottenToken = async ({
  email,
  language = 'da',
}: {
  email: string;
  language?: UserManagementLanguage;
}) => {
  appLog('createUserPasswordForgottenToken #1', { email, language });

  const res = await apiCweb.post(
    '/user_management/tokens/user_password_forgotten_token',
    { email },
    {
      params: {
        verify_code_email_language: language,
      },
    }
  );

  appLog('createUserPasswordForgottenToken #2 ->', res.data);
  return res.data;
};

/**
 * POST /user_management/tokens/user_password_change_token
 * Create token for user password change
 */
export const createUserPasswordChangeToken = async ({
  language = 'da',
}: {
  language?: UserManagementLanguage;
}) => {
  appLog('createUserPasswordChangeToken #1', { language });

  const res = await apiCweb.post(
    '/user_management/tokens/user_password_change_token',
    {},
    {
      params: {
        verify_code_email_language: language,
      },
    }
  );

  appLog('createUserPasswordChangeToken #2 ->', res.data);
  return res.data;
};

/**
 * PUT /user_management/tokens/verification_emails?action=resend
 * Resend verification email
 */
export const resendUserManagementVerificationEmail = async ({
  publicKey,
  language = 'da',
}: {
  publicKey: string;
  language?: UserManagementLanguage;
}): Promise<void> => {
  appLog('resendUserManagementVerificationEmail #1', {
    publicKey,
    language,
  });

  const res = await apiCweb.put(
    '/user_management/tokens/verification_emails',
    {},
    {
      headers: {
        'Public-Key': publicKey,
      },
      params: {
        action: 'resend',
        verify_code_email_language: language,
      },
    }
  );

  appLog('resendUserManagementVerificationEmail #2 ->', res.status);
};

/**
 * POST /user_management/tokens/validation
 * Validate token -> get private key
 */
export const validateUserManagementToken = async ({
  publicKey,
  simpleKey,
}: {
  publicKey: string;
  simpleKey: string;
}) => {
  appLog('validateUserManagementToken #1', {
    publicKey,
    simpleKey,
  });

  const res = await apiCweb.post(
    '/user_management/tokens/validation',
    {},
    {
      headers: {
        'Public-Key': publicKey,
        'Simple-Key': simpleKey,
      },
    }
  );

  appLog('validateUserManagementToken #2 ->', res.data);
  return res.data;
};

/**
 * PUT /user_management/password/forgot
 * Reset user password with private key
 */
export const resetUserPasswordWithPrivateKey = async ({
  privateKey,
  newPassword,
}: {
  privateKey: string;
  newPassword: string;
}): Promise<void> => {
  appLog('resetUserPasswordWithPrivateKey #1', {
    privateKey,
  });

  const res = await apiCweb.put(
    '/user_management/password/forgot',
    {
      new_password: newPassword,
    },
    {
      headers: {
        'Private-Key': privateKey,
      },
    }
  );

  appLog('resetUserPasswordWithPrivateKey #2 ->', res.status);
};

/**
 * PUT /user_management/password/change
 * Change user password with private key
 */
export const changeUserPasswordWithPrivateKey = async ({
  privateKey,
  oldPassword,
  newPassword,
}: {
  privateKey: string;
  oldPassword: string;
  newPassword: string;
}): Promise<void> => {
  appLog('changeUserPasswordWithPrivateKey #1', {
    privateKey,
  });

  const res = await apiCweb.put(
    '/user_management/password/change',
    {
      old_password: oldPassword,
      new_password: newPassword,
    },
    {
      headers: {
        'Private-Key': privateKey,
      },
    }
  );

  appLog('changeUserPasswordWithPrivateKey #2 ->', res.status);
};
